import React, { useState, useEffect } from "react";

import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import { isEmpty } from "lodash";
import Tippy from "@tippyjs/react";

import CardTable from "../../components/Tables/CardTable";
import LotAPI from "../../API/lots.api";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import Pagination from "../../components/Pagination/Pagination";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import { getCurrentDateYMD } from "../../utils/DateHelper";
import LookupsAPI from "../../API/lookups.api";
import LotModal from "../../components/Modals/LotModal";
import { longDate } from "../../utils/DateHelper";
import { generateGoogleMapsUrl } from "../../utils/mapsHelpers";
import { getSessionFilters, getMappedFilters } from "../../utils/FilterHelpers";
import { Trans, useTranslation } from "react-i18next";
import { ROLES } from "../../utils/constants";
import { isAuthorized } from "../../utils/RolesHelpers";
import { getRoles } from "../../utils/OktaHelpers";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { handleExternalLink } from "../../utils/ExternalLinksHelper";
import { setObject, getObject } from "../../utils/StorageHelpers";
import { mapLookupToSelect } from "../../utils/Helpers";
import MissingData from "../../components/Shared/MissingData";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";

const Lots = () => {
    const [organizations, setOrganizations] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [isRendered, setIsRendered] = useState(false);
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const [filters, setFilters] = useState({});
    //React lazy initialization in to reduce the cost of useless calling of getSessionFilters
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters("lots-filters")
    );
    const [filtersUsed, setFiltersUsed] = useState(false);

    const [lots, setLots] = useState();
    const [lotsCount, setLotsCount] = useState(0);
    const [lotModal, setLotModal] = useState(false);
    const [toBeEditedLot, setToBeEditedLot] = useState(null);
    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Lots-itemsPerPage", true) || 10
    );

    const { t } = useTranslation();

    const keys = [
        { name: "name" },
        {
            name: "description",
            element: (row) => {
                return row.description ? (
                    <Tippy
                        content={row.description}
                        placement="top"
                        animation="scale"
                    >
                        <span tabIndex="0" className="tooltip-container">
                            {row.description}
                        </span>
                    </Tippy>
                ) : (
                    "---"
                );
            },
        },
        { name: "material", subKey: "name" },
        {
            name: "location",
            element: (row) => {
                const mapUrl = generateGoogleMapsUrl(row);
                return mapUrl ? (
                    <div
                        onClick={() => handleExternalLink(mapUrl)}
                        className="w-40 overflow-hidden block overflow-ellipsis whitespace-nowrap underline text-blue-600 cursor-pointer"
                    >
                        {mapUrl}
                    </div>
                ) : (
                    <>---</>
                );
            },
        },
        {
            name: "organization",
            subKey: "name",
        },
        {
            name: "createdBy",
            element: (row) => {
                return row.createdBy ? (
                    <Tippy
                        content={row.createdBy}
                        placement="top"
                        animation="scale"
                    >
                        <span
                            tabIndex="0"
                            className="description-oneline tooltip-container"
                        >
                            {row.createdBy}
                        </span>
                    </Tippy>
                ) : (
                    "---"
                );
            },
        },
        { name: "createdAt", parseMethod: (date) => longDate(date) },
        {
            name: "deployed",
            element: (row) => (
                <Tippy
                    content="This Lot has a deployed material"
                    placement="top"
                    animation="scale"
                >
                    <div
                        className={`tag-ele tag-${
                            row.deployed ? "deployed" : "in progress"
                        } px-2`}
                    >
                        {row.deployed ? t("true") : t("false")}
                    </div>
                </Tippy>
            ),
        },
    ];
    const headers = [
        { label: t("name"), name: "name" },
        { label: t("description"), name: "description", skipSort: true },
        { label: t("material"), name: "materialName" },
        { label: t("location"), name: "location", skipSort: true },
        {
            label: t("affiliate"),
            name: "organization",
            skipSort: true,
        },
        { label: t("createdBy"), name: "createdBy", skipSort: true },
        { label: t("createdAt"), name: "createdAt" },
        { label: t("isDeployed"), name: "deployed", skipSort: true },
        {
            label: t("actions"),
            skipSort: true,
            role: !isAuthorized(
                [ROLES.support, ROLES.partnerAdmin],
                getRoles()
            ),
        },
    ];
    const searchFilters = [
        {
            label: t("name"),
            name: "name",
            type: "Input",
            placeholder: t("lotPlaceholder"),
        },
        {
            label: t("material"),
            name: "materialName",
            type: "Input",
            placeholder: t("materialNamePlaceholder"),
        },
        {
            label: t("createdBy"),
            name: "createdBy",
            type: "Input",
            placeholder: t("emailPlaceholder"),
        },
        {
            label: t("affiliate"),
            name: "organizationId",
            type: "Dropdown",
            options: organizations,
        },
        {
            label: t("createdAt"),
            name: "createdAt",
            type: "Date",
            max: getCurrentDateYMD(),
        },
    ];

    useEffect(() => {
        const localStorageFilters = getSessionFilters("lots-filters");
        if (!isEmpty(localStorageFilters)) {
            setFilters(localStorageFilters);
            setAppliedFilters(localStorageFilters);
        }
        fetchOrganizations();
        setIsRendered(true);
    }, []);

    useEffect(() => {
        if (isRendered) {
            if (sortBy && sortDirection) {
                setCurrentPage(1);
                fetchLots(appliedFilters, sortBy, sortDirection);
            } else {
                setCurrentPage(1);
                fetchLots(appliedFilters);
            }
        }
    }, [sortBy, sortDirection, isRendered]);

    const fetchLots = async (
        _filters,
        _sortBy = null,
        _sortDirection = null,
        offset = 0,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsLoading(true);
        const newFilter = getMappedFilters(_filters);
        const _offset = offset < 0 ? 0 : offset; // to ensure you don't get a negative offset

        const result = await LotAPI.getLots(
            itemsPerPage,
            newFilter,
            _offset,
            _sortBy,
            _sortDirection
        );
        setLots(result?.data || []);
        setLotsCount(result?.totalCount || 0);
        setIsLoading(false);
    };

    const fetchOrganizations = async () => {
        let result = await LookupsAPI.getOrganizations();
        setOrganizations(mapLookupToSelect(result) || []);
    };

    const handleDeleteLot = async (index) => {
        try {
            const result = await LotAPI.deleteLot(lots[index].id);
            if (!isEmpty(result)) {
                SuccessAlert(t("deleteLotSuccessMessage"));
                LogCapture.info(
                    `Lot deleted with name: ${lots[index]?.name} & id: ${lots[index]?.id}`,
                    ENTITIES.lots,
                    ACTIONS.lots.delete
                );

                let newCurrentPage = currentPage;
                if (lots.length <= 1 && currentPage > 1) {
                    newCurrentPage = currentPage - 1;
                }
                setCurrentPage(newCurrentPage);
                fetchLots(filters, sortBy, sortDirection, newCurrentPage - 1);
                return true;
            } else {
                // ErrorAlert(t("deleteLotFailMessage"));
                LogCapture.error(
                    `Couldn't delete lot with name: ${lots[index]?.name} & id: ${lots[index]?.id}`
                );
                return false;
            }
        } catch (error) {
            LogCapture.error(
                `Couldn't delete lot with name: ${lots[index]?.name} & id: ${lots[index]?.id}, error: ${error}`
            );
            ErrorAlert(error);
        }
    };

    const handleEditClicked = (index) => {
        setLotModal(true);
        setToBeEditedLot(lots[index]);
    };

    const applyFilters = (specificAppliedFilters) => {
        fetchLots(specificAppliedFilters, sortBy, sortDirection);
        setFiltersUsed(true);
    };

    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            fetchLots(filters, sortBy, sortDirection, index - 1);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Lots-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        fetchLots(filters, sortBy, sortDirection, 0, itemsPerPage);
    };

    const completionHandler = () => {
        fetchLots(appliedFilters, sortBy, sortDirection);
    };

    return (
        <>
            <header className="main-content-header flex items-center justify-between">
                <h1 className="h1">
                    <Trans i18nKey="lotManagement">Lot Management</Trans>
                </h1>
                <div className="action-header-wrapper flex items-center">
                    <FilterDropdown
                        filterOptions={searchFilters}
                        filters={filters}
                        setFilters={setFilters}
                        applyFilters={applyFilters}
                        component="lots"
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                    />
                    {isAuthorized(
                        [
                            ROLES.partnerAdmin,
                            ROLES.support,
                            ROLES.partnerUser,
                            ROLES.admin,
                            ROLES.user,
                        ],
                        getRoles()
                    ) && (
                        <button
                            className="default-orange-outline-btn ml-2"
                            onClick={() => {
                                setLotModal(true);
                            }}
                        >
                            <i className="fas fa-plus mr-2" />
                            <Trans i18nKey="addNewLot">New Lot</Trans>
                        </button>
                    )}
                </div>
            </header>
            <LoadingOverlay active={isLoading}>
                <div className="card-table shadow">
                    {lotsCount > 0 ? (
                        <>
                            <CardTable
                                keys={keys}
                                headers={headers}
                                rows={lots}
                                handleDelete={handleDeleteLot}
                                handleEditClicked={handleEditClicked}
                                purpose={"lots"}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}
                                actionColumn={isAuthorized(
                                    [ROLES.support, ROLES.partnerAdmin],
                                    getRoles()
                                )}
                                hasEditAccess={isAuthorized(
                                    [ROLES.support, ROLES.partnerAdmin],
                                    getRoles()
                                )}
                                hasDeleteAccess={isAuthorized(
                                    [ROLES.support, ROLES.partnerAdmin],
                                    getRoles()
                                )}
                            />

                            <Pagination
                                currentPage={currentPage}
                                setCurrentPage={handlePageClick}
                                totalCount={lotsCount}
                                sizePerPage={itemsPerRequest}
                                handleItemsPerPage={handleItemsPerPage}
                            />
                        </>
                    ) : (
                        <MissingData
                            isFilterUsed={filtersUsed}
                            entity={t("lots")}
                            includeText={!isLoading}
                            clickCallback={() => setLotModal(true)}
                        />
                    )}
                </div>
            </LoadingOverlay>
            <LotModal
                modalButton={lotModal}
                modalButtonSetter={setLotModal}
                completionHandler={completionHandler}
                data={toBeEditedLot}
                setData={setToBeEditedLot}
            />
        </>
    );
};

export default Lots;
